import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationsAPI } from '../../../services/api';
import { SELECTABLE_TIMEZONES } from '../../../utils/timezoneMap';

// Gating por rol (mismo patrón de props que MembersTab/InvitationsTab): la
// danger-zone es solo OWNER y editar el nombre exige canManageOrgSettings —
// espeja los guards del backend (DELETE /:orgId → requireRole('owner'),
// PATCH /:orgId → requirePermission('canManageOrgSettings')), que ya
// protegían: esto es presentación, no seguridad.
export default function GeneralTab({ organization, onReload, currentUserRole, canManageOrgSettings }) {
  const { t } = useTranslation('organization');
  const isOwner = currentUserRole === 'owner';
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(organization?.name || '');
  const [saving, setSaving] = useState(false);
  // Estado local optimista: context.organization no es reactivo (se carga una
  // vez), así que el select refleja la selección al instante y revierte si el
  // PATCH falla. '' = heredar (timeZone null → país → default).
  const [tz, setTz] = useState(organization?.timeZone || '');
  const [savingTz, setSavingTz] = useState(false);

  const handleTimezoneChange = async (value) => {
    const previous = tz;
    setTz(value);
    setSavingTz(true);
    try {
      await organizationsAPI.update(organization.id, { timeZone: value || null });
    } catch (error) {
      console.error('Error updating org timezone:', error);
      setTz(previous); // revertir la selección si el backend rechazó
      alert(t('general.timezoneUpdateError') + (error.message || ''));
    } finally {
      setSavingTz(false);
    }
  };

  const handleSave = async () => {
    if (!newName.trim()) {
      alert(t('general.emptyNameError'));
      return;
    }

    try {
      setSaving(true);
      await organizationsAPI.update(organization.id, { name: newName });
      setEditing(false);
      if (onReload) await onReload();
      alert(t('general.nameUpdated'));
    } catch (error) {
      console.error('Error updating name:', error);
      alert(t('general.nameUpdateError') + (error.message || t('general.unknownError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmMessage = t('general.deleteConfirmPrompt');
    const userInput = window.prompt(confirmMessage);

    if (userInput !== 'ELIMINAR') {
      return;
    }

    try {
      await organizationsAPI.delete(organization.id);
      alert(t('general.deleteSuccess'));
      window.location.href = '/dashboard'; // Redirect to dashboard
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert(t('general.deleteError') + (error.message || t('general.unknownError')));
    }
  };

  if (!organization) {
    return <div>{t('general.noOrganization')}</div>;
  }

  return (
    <div className="general-tab">
      <section className="org-info">
        <h2>{t('general.title')}</h2>

        <div className="info-row">
          <label>{t('general.labelName')}</label>
          {editing ? (
            <div className="edit-controls">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={saving}
                autoFocus
              />
              <button
                className="btn-primary btn-sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t('general.saving') : t('general.save')}
              </button>
              <button
                className="btn-secondary btn-sm"
                onClick={() => {
                  setEditing(false);
                  setNewName(organization.name);
                }}
                disabled={saving}
              >
                {t('general.cancel')}
              </button>
            </div>
          ) : (
            <div className="view-controls">
              <span className="org-value">{organization.name}</span>
              {canManageOrgSettings && (
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => setEditing(true)}
                >
                  {t('general.edit')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="info-row">
          <label>{t('general.labelType')}</label>
          <span className="org-value">{organization.type || 'team'}</span>
        </div>

        <div className="info-row">
          <label>{t('general.labelPlan')}</label>
          <span className="org-value">{organization.planId || 'starter'}</span>
        </div>

        <div className="info-row">
          <label>{t('general.labelSlots')}</label>
          <span className="org-value">
            {organization.seats?.available || 0} / {organization.seats?.total || 0}
          </span>
        </div>

        <div className="info-row">
          <label>{t('general.timezoneLabel')}</label>
          {canManageOrgSettings ? (
            <div className="view-controls">
              {/* Zonas desde SELECTABLE_TIMEZONES (fuente única timezoneMap —
                  misma que la whitelist del PATCH y el mapa de inferencia).
                  Jerarquía efectiva: bot config (por-bot) > org (este
                  selector) > país > default */}
              <select
                value={tz}
                disabled={savingTz}
                onChange={(e) => handleTimezoneChange(e.target.value)}
              >
                <option value="">{t('general.timezoneInherit')}</option>
                {SELECTABLE_TIMEZONES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          ) : (
            <span className="org-value">{tz || t('general.timezoneInherit')}</span>
          )}
        </div>
        <div className="info-row">
          <small style={{ color: '#718096', fontSize: '0.8rem' }}>
            {t('general.timezoneHelp')}
          </small>
        </div>

        <div className="info-row">
          <label>{t('general.labelCreated')}</label>
          <span className="org-value">
            {organization.createdAt
              ? (() => {
                  // Handle both Firestore Timestamp and serialized dates from API
                  try {
                    if (organization.createdAt.toDate) {
                      // Firestore Timestamp
                      return new Date(organization.createdAt.toDate()).toLocaleDateString();
                    } else if (organization.createdAt._seconds) {
                      // Serialized Timestamp from backend
                      return new Date(organization.createdAt._seconds * 1000).toLocaleDateString();
                    } else {
                      // Regular date string or timestamp
                      return new Date(organization.createdAt).toLocaleDateString();
                    }
                  } catch (e) {
                    console.error('Error parsing createdAt:', e, organization.createdAt);
                    return 'N/A';
                  }
                })()
              : 'N/A'}
          </span>
        </div>
      </section>

      {isOwner && (
        <section className="danger-zone">
          <h2>{t('general.dangerZoneTitle')}</h2>
          <p className="warning">
            {t('general.dangerZoneWarning')}
          </p>
          <button className="btn-danger" onClick={handleDelete}>
            {t('general.deleteButton')}
          </button>
        </section>
      )}
    </div>
  );
}
